/**
 * How a body is disposed of, keyed by what killed it.
 *
 * The brief was "a death animation, preferably related to the reason for
 * death". The thing that makes that cheap here is that **the cause already
 * selects how the body's own matter leaves it**, and the material simulation
 * already knows what to do with matter in transit. So there is no animation in
 * this file and no frames: there is a table of settings for three primitives,
 * and the primitives are the ones the body system already had or nearly had.
 *
 * ==== THE THREE PRIMITIVES ==============================================
 *
 * 1. **char** — `pixelBody.charBody`. Walk the three tissue materials along one
 *    of the material table's own product relations (`ashesInto`,
 *    `freezesInto`, `shatteredInto`). This is `scorchBody` with the relation
 *    lifted out of the two booleans it was hardcoded into.
 *
 * 2. **shed** — `pixelBody.shedBody`. Clear pixels from the damage mask and
 *    hand each one to the caller, which emits it as a real particle. This is
 *    `woundBody` with the impact circle replaced by a *scan order*, because
 *    what distinguishes an acid death from a fire death is not where the body
 *    was hit — nothing hit it — but which of its pixels go first.
 *
 * 3. **collapse** — `pixelBody.collapseSkeleton`. `y' = fold * y`,
 *    `x' = widen * x` on the resolved joints. Exactly the affine that
 *    `poseSkeleton` already applies for the crouch (`crouchPose`), driven
 *    continuously and with the feet line as its fixed point, so a body folds
 *    onto the ground it is standing on rather than sinking through it.
 *
 * ==== AND THE FIVE READINGS THEY COMPOSE INTO ===========================
 *
 * Five, not twelve, because twelve damage types do not produce twelve legible
 * silhouettes — they produce five, and each of the five turns exactly one of
 * the three knobs to an extreme:
 *
 * | rite       | char      | shed              | collapse        |
 * |------------|-----------|-------------------|-----------------|
 * | `char`     | to ash    | edge, slow, rising| slack           |
 * | `rime`     | to frost  | **burst**, fast   | none — it stands|
 * | `dissolve` | none      | **bottom-up**     | deep, tracks it |
 * | `sunder`   | to shards | burst, wide, low  | **flattened**   |
 * | `slack`    | none      | edge, few         | slack           |
 *
 * A body that burns chars and blows away upward. A frozen body does not slump
 * at all — it stands there and then comes apart at once, which is the whole
 * reason `rime` has a hold before its shed. Acid eats a body from the puddle
 * up, so the shed order is the one thing that carries it and the collapse
 * follows the loss rather than leading it. Shock and void take the body apart
 * and leave it spread on the floor. Everything else goes limp.
 *
 * ==== WHAT SELECTS THE RITE, AND WHAT DELIBERATELY DOES NOT =============
 *
 * Two things, in this order, and `riteFor` at the bottom is both of them:
 *
 *   1. **the run's outcome kind**, as a gate rather than as a choice — an
 *      ending that is not a death selects *no rite at all* and returns null;
 *   2. `hasStatus(w, i, FROZEN)`, then `DeathCause.damage`.
 *
 * Not `agent`, which is a display noun, and **not `material`, which is null
 * for every player death the game can currently produce** — see the comment
 * below. Selecting on a field that is always null would author four fifths of
 * a table nothing reaches, which is the dead-field defect this project keeps
 * finding.
 *
 * The map is total over `DAMAGE_TYPE_NAMES` and `tests/deathRite.test.ts`
 * asserts it, in both directions: a new damage type with no rite fails, and a
 * rite no damage type reaches fails.
 */

import type { DeathCause, RunOutcome } from '../meta/runState';
import { StatusKind } from '../combat/types';
import { hasStatus } from '../combat/status';
import type { World } from './ecs';
import type { Residue, ShedOrder } from './pixelBody';

/*
 * Why `DeathCause.material` is not read here, written down rather than left as
 * an absence somebody re-adds.
 *
 * `Game.damageEntity`'s fifth parameter is `srcMat`, and `DeathCause.material`
 * is `srcMat > 0 ? materialDef(srcMat).label : null`. There is exactly one
 * call site in the game that passes a non-zero `srcMat`: the rigid-body crush
 * sink in `Game`'s constructor, which passes `MAT.WOOD`. And `RigidBodies.
 * applyCrush` declines the player outright — docs/01 §5.1's asymmetry, counted
 * by `playerCrushesDeclined`.
 *
 * So: no reachable player death carries a material, every environmental death
 * reads "Killed by the world" rather than naming the lava, and a rite selected
 * on `material` would be a rite nothing selects. That is a defect in the
 * damage plumbing, not in this table, and it is reported rather than worked
 * around.
 *
 * This was an exported `const MATERIAL_IS_NOT_READ = true` — a value nothing
 * read, asserting nothing, existing to hold a comment. That is the dead field
 * the paragraph above is complaining about, one file over. It is a comment.
 */

export type RiteId = 'char' | 'rime' | 'dissolve' | 'sunder' | 'slack';

export interface DeathRite {
  readonly id: RiteId;
  /**
   * How long the rite runs, in **simulation ticks** — 60 of them a second.
   *
   * Ticks and not seconds, and stepped from `Game.stepSimulation` and not from
   * the render loop, because the rite emits matter: a shed budget spent per
   * rendered frame would put a different amount of the player's body into the
   * world on a 144 Hz monitor than on a 60 Hz one. Same reason `sim/cellsim.ts`
   * refuses `performance.now()`.
   */
  readonly ticks: number;
  /** Product relation the tissue palette walks along, or null to leave it. */
  readonly residue: Residue | null;
  /** Which pixels go first. */
  readonly order: ShedOrder;
  /**
   * Tick the shed starts on.
   *
   * **Never earlier than `collapseTicks`, and that is a correctness constraint
   * rather than a taste one.** `PixelBody.intact` — the damage mask both
   * `woundBody` and `shedBody` write — is indexed in **raster space**, not in
   * body space. It has to be: it is what makes a wound survive the next pose.
   * But the collapse *moves the body through the raster*, so a pixel shed off
   * the head at tick 4 leaves a permanent hole at that raster row, and thirty
   * ticks later the folding torso arrives at that row and is deleted by it.
   *
   * Measured, before this constraint existed: `slack` sheds 14% of the body and
   * the duelist finished with 20 of its 128 pixels — 68 of them **erased rather
   * than shed**, so they never became particles and the conservation the whole
   * design rests on was quietly false. It was not visible in any assertion,
   * because the conservation test compared particles to `body.lost` and
   * `body.lost` only counts what the shed took.
   *
   * Sequencing the two removes the class entirely: nothing is masked until the
   * fold has stopped moving, so the silhouette under the mask is static and
   * `lit === total - lost` exactly. `tests/deathRite.test.ts` asserts both the
   * ordering and the identity. It also reads better than the overlap did — the
   * body falls, and *then* it comes apart.
   */
  readonly shedFrom: number;
  /** Ticks the shed is spread over. The budget denominator. */
  readonly shedTicks: number;
  /** Fraction of the body that leaves, as a fraction of its baked pixel count. */
  readonly shedFraction: number;
  /** Launch speed, cells/sec. */
  readonly speed: number;
  /** Launch cone half-angle, radians. */
  readonly cone: number;
  /** Vertical launch bias. Negative rises. Ignored by `burst`, which is radial. */
  readonly lift: number;
  /** Added to the shed matter's spawn temperature, °C. */
  readonly tempBias: number;
  /** Where the fold ends. 1 is no collapse at all. */
  readonly fold: number;
  /** Where the widen ends. Capped at raster time; see `collapseSkeleton`. */
  readonly widen: number;
  /** Ticks the collapse takes to reach `fold`/`widen`. */
  readonly collapseTicks: number;
}

/**
 * The table.
 *
 * The durations are the one place a feel judgement is made, and they are made
 * against the death screen rather than against nothing: docs/23 §2.1 is two
 * lines over a live world, and phase two does not arrive until the player asks
 * for it. So the rite has as long as it wants — the longest is 2.8 s — and the
 * argument for each length is legibility, not budget. `tests/deathRite.test.ts`
 * dumps every one of them as frames.
 */
export const RITES: Readonly<Record<RiteId, DeathRite>> = {
  /** Burned. Chars to its own ash product and blows away upward, over 2.8 s. */
  char: {
    id: 'char',
    ticks: 168,
    residue: 'ash',
    order: 'edge',
    shedFrom: 54,
    shedTicks: 108,
    shedFraction: 0.85,
    speed: 26,
    cone: 0.9,
    lift: -1,
    // Ash leaving a body that is on fire is hot ash. It lands, and it goes on
    // being hot in the grid, which is the point of shedding real matter.
    tempBias: 260,
    fold: 0.42,
    widen: 1.05,
    collapseTicks: 54,
  },

  /**
   * Frozen. **The only rite with no collapse**, and the only one with a hold.
   *
   * A frozen body does not go slack; it stays exactly where it was standing and
   * then stops being one piece. `fold: 1` is that, expressed as the identity of
   * the collapse primitive rather than as a branch around it.
   */
  rime: {
    id: 'rime',
    ticks: 96,
    residue: 'frost',
    order: 'burst',
    shedFrom: 36,
    shedTicks: 14,
    shedFraction: 1,
    speed: 120,
    // Wide, but not a random direction. `Math.PI` — which this was — makes the
    // launch angle uniform over the circle, and a uniform launch angle is
    // indistinguishable from "the pixel went somewhere": the mean radial
    // component is zero and the shatter reads as a puff rather than as
    // something expanding. 1.3 rad keeps 74% of the speed pointing away from
    // the body while still spraying.
    cone: 1.3,
    lift: 0,
    tempBias: -40,
    fold: 1,
    widen: 1,
    collapseTicks: 1,
  },

  /**
   * Acid. Eaten from the floor up: `sink` takes the lowest occupied rows first,
   * so the legs go and the body settles into the space they were holding it out
   * of. Nothing is charred — acid leaves flesh as flesh, it just leaves less
   * of it — so the whole reading is carried by the shed order.
   */
  dissolve: {
    id: 'dissolve',
    ticks: 168,
    residue: null,
    order: 'sink',
    shedFrom: 48,
    shedTicks: 114,
    shedFraction: 1,
    speed: 14,
    cone: 0.5,
    lift: 0.3,
    tempBias: 0,
    fold: 0.16,
    widen: 1.2,
    collapseTicks: 48,
  },

  /**
   * Shock, void, nanite. The fast one: the body comes apart in 0.2 s and what
   * is left of it is flat and wide. This is the rite the brief called a crush,
   * and it is keyed to the damage types that can actually reach the player —
   * see `MATERIAL_IS_NOT_READ`.
   */
  sunder: {
    id: 'sunder',
    ticks: 44,
    residue: 'shatter',
    order: 'burst',
    shedFrom: 16,
    shedTicks: 12,
    shedFraction: 0.55,
    speed: 95,
    cone: 1.1,
    lift: 0.25,
    tempBias: 0,
    fold: 0.18,
    widen: 1.35,
    collapseTicks: 16,
  },

  /**
   * A blow. No transformation, very little matter lost, and the whole reading
   * is the posture going: `Pose.DEAD` plus a fold to 40%.
   *
   * This is the default and it is also the most common death, so it is the one
   * that must not look like an effect.
   */
  slack: {
    id: 'slack',
    ticks: 96,
    residue: null,
    order: 'edge',
    shedFrom: 34,
    shedTicks: 40,
    shedFraction: 0.14,
    speed: 30,
    cone: 0.8,
    lift: -0.2,
    tempBias: 0,
    fold: 0.4,
    widen: 1.08,
    collapseTicks: 34,
  },
};

/**
 * Damage type name -> rite. Total over `entity/ecs.ts`'s `DAMAGE_TYPE_NAMES`,
 * and asserted total in both directions by the tests.
 *
 * Keyed by the *name* rather than by the numeric `DamageType`, because that is
 * what `DeathCause.damage` carries — the cause record is already the join of
 * the two, and re-deriving the id from the name here would be a second place
 * for the vocabulary to be spelled.
 */
export const RITE_BY_DAMAGE: Readonly<Record<string, RiteId>> = {
  // Heat, and the one damage type that burns as an article of faith.
  fire: 'char',
  radiant: 'char',
  // Cold.
  frost: 'rime',
  // Things that consume matter rather than transforming it.
  acid: 'dissolve',
  toxic: 'dissolve',
  fungal: 'dissolve',
  // Things that take a body apart.
  shock: 'sunder',
  void: 'sunder',
  nanite: 'sunder',
  // Blunt force, blood loss, and being unmade by time — all of which end with
  // a body on the floor and nothing else to say about it.
  physical: 'slack',
  blood: 'slack',
  temporal: 'slack',
};

/**
 * The rite for a cause, ignoring the body's state and the run's outcome.
 *
 * `null` — a death recorded before `DeathCause` existed, i.e. a v4 save —
 * gets `slack`, which is the reading that claims the least. This is the pure
 * half of the selection and it is exported for the tests and for `riteFor`;
 * the game calls `riteFor`.
 */
export function riteForCause(cause: Pick<DeathCause, 'damage'> | null): DeathRite {
  if (cause === null) return RITES.slack;
  return RITES[RITE_BY_DAMAGE[cause.damage] ?? 'slack'];
}

/**
 * The rite this run's ending earns, or **null for an ending that is not a
 * death**.
 *
 * ==== THE GATE, AND WHY IT IS IN THIS FILE ==============================
 *
 * `outcome.kind !== 'death'` returns null and nothing is disposed of. That is
 * not defensive coding; it is the whole correctness content of #108.
 *
 * Before #108, walking away called `finishRun('death', …)` and abandon was
 * banked as a death. #108 made it `finishRun('abandon', null, null)` — one
 * funnel, three kinds — which means `finishRun` is the natural place to start
 * the rite and is also the place where a rite would be started for an ending
 * that never killed anybody. An abandoned run has `cause: null`, so a rite
 * selected on the cause alone would land on `slack` and play a body going
 * limp for a player who pressed Quit while standing up with full HP.
 *
 * The gate is here rather than at the call site because the call site is the
 * integrator, and a `if (kind === 'death')` in `Game.finishRun` is a condition
 * a later refactor can drop without any test in this file noticing. Expressed
 * as this function's return type it is unavoidable: the caller holds a
 * `DeathRite | null` and has to say what it does with the null.
 *
 * ==== AND THE FROZEN OVERRIDE ===========================================
 *
 * `hasStatus(w, i, StatusKind.FROZEN)` selects `rime` whatever the killing
 * blow was, which is **the same predicate `enemyAI.ts`'s `killEnemy` already
 * uses** to decide a body comes apart into its `shatteredInto` product rather
 * than its corpse material. There is deliberately no second notion of being
 * shattered here: a frozen body that is then hit with a hammer shatters, and
 * `resolve.ts`'s `shatterFrozen` says so for the damage number in exactly the
 * same terms. `RITE_BY_DAMAGE.frost` stays, because frost damage kills bodies
 * that never accumulated the status.
 *
 * The override reads the *live* entity rather than the recorded cause because
 * the status is not in the cause and cannot be: `DeathCause` is four display
 * fields (docs/23 §3), and adding a fifth for this would put a simulation fact
 * in a record whose whole job is what to say. The entity is still alive and
 * still frozen at the moment `finishRun` runs — docs/23 §7.5 keeps the body —
 * so the fact is there to be read where it lives.
 */
export function riteFor(
  outcome: Pick<RunOutcome, 'kind' | 'cause'> | null,
  w: World,
  i: number,
): DeathRite | null {
  if (outcome === null || outcome.kind !== 'death') return null;
  if (hasStatus(w, i, StatusKind.FROZEN)) return RITES.rime;
  return riteForCause(outcome.cause ?? null);
}
